import { Check, ExternalLink } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { VersionedTransaction } from '@solana/web3.js';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import tokenSkrIcon from '../../assets/skr-logo.svg';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTRPC } from '../../hooks/useTRPC';
import { getErrorMessage } from '../../utils/errorHandling';
import { SOLANA_CLUSTER, SOLANA_IS_MAINNET } from '../../utils/solanaUtils';
import { Button } from '../Button';
import { LoadingIcon } from '../LoadingIcon';
import {
  PortalCard,
  PortalPage,
  PortalPageHeader,
  Skeleton,
  SkeletonGroup,
} from '../portal';

const LINK_CLASSES =
  'rounded underline transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

const STAKE_URL = 'https://stake.solanamobile.com';

type ClaimState =
  | 'loading'
  | 'not_connected'
  | 'eligible'
  | 'not_eligible'
  | 'already_claimed'
  | 'claiming'
  | 'success'
  | 'error';

interface ClaimData {
  tokenAmount: number;
}

interface ClaimConfig {
  claimEnabled: boolean;
  claimMessage: string | null;
}

const formatTokenAmount = (amount: number) => {
  return amount.toLocaleString();
};

const NetworkChip = () => {
  if (SOLANA_IS_MAINNET) return null;

  return (
    <div className="fixed bottom-4 right-4 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg shadow-lg flex items-center gap-2 z-50">
      <span className="size-2 bg-green-400 rounded-full animate-pulse" />
      <span className="uppercase">{SOLANA_CLUSTER}</span>
    </div>
  );
};

const NotConnectedView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Claim Your SKR</h2>
        <p className="text-seeker-400 text-center">
          Connect your wallet to check your eligibility for the SKR airdrop.
        </p>
      </div>
    </div>
  );
};

/** Mirrors `ClaimView` (the most likely next state): title, 2-line copy, allocation card. */
const LoadingView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex w-full max-w-md flex-col items-center">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">
          Checking Eligibility
        </h2>
        <SkeletonGroup
          label="Checking eligibility"
          className="flex w-full flex-col items-center"
        >
          <div className="mb-8 flex w-full max-w-sm flex-col items-center">
            <div className="flex h-6 w-full items-center">
              <Skeleton className="h-4 w-full" />
            </div>
            <div className="flex h-6 items-center">
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <PortalCard className="flex w-full flex-col items-center p-6">
            <div className="flex h-5 items-center">
              <Skeleton className="h-3.5 w-20" />
            </div>
            <div className="my-2 flex h-10 items-center">
              <Skeleton className="h-8 w-40" rounded="lg" />
            </div>
            <div className="mt-6 w-full rounded-lg bg-seeker-800 p-4">
              <Skeleton className="h-10 w-full bg-seeker-700" rounded="lg" />
            </div>
          </PortalCard>
        </SkeletonGroup>
      </div>
    </div>
  );
};

const NotEligibleView = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)] grayscale opacity-50"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Not Eligible</h2>
        <p className="text-seeker-400 text-center mb-8">
          This wallet is not eligible for the SKR airdrop. Please connect a
          different wallet if you believe you should be eligible.
        </p>

        <WalletMultiButton />
      </div>
    </div>
  );
};

const AlreadyClaimedView = ({ tokenAmount }: { tokenAmount: number }) => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Already Claimed</h2>
        <p className="text-seeker-400 text-center">
          You have already claimed{' '}
          <span className="tabular-nums">{formatTokenAmount(tokenAmount)}</span>{' '}
          SKR with this wallet.
        </p>
      </div>
    </div>
  );
};

const ErrorView = ({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) => {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">
          Something Went Wrong
        </h2>
        <p className="text-red-400 text-center mb-8">{error}</p>

        <Button variant="primary" onClick={onRetry} className="min-w-[180px]">
          Try Again
        </Button>
      </div>
    </div>
  );
};

const ClaimView = ({
  claimData,
  claimConfig,
  onClaim,
  isClaiming,
}: {
  claimData: ClaimData;
  claimConfig: ClaimConfig;
  onClaim: () => void;
  isClaiming: boolean;
}) => {
  const isDisabled = !claimConfig.claimEnabled || isClaiming;

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4">
      <div className="flex flex-col items-center max-w-md">
        <div className="mb-6">
          <img
            src={tokenSkrIcon}
            alt="SKR Token"
            className="size-24 drop-shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
          />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Claim Your SKR</h2>
        <p className="text-seeker-400 text-center mb-8">
          As a dedicated Solana Mobile publisher, we appreciate your work and
          are rewarding your contributions.
        </p>

        <PortalCard className="w-full p-6">
          <div className="text-center">
            <span className="text-seeker-400 text-sm">Allocation</span>
            <div className="text-4xl font-bold leading-10 text-white my-2 tabular-nums">
              {formatTokenAmount(claimData.tokenAmount)} SKR
            </div>

            {claimConfig.claimEnabled && (
              <p className="text-seeker-400 text-sm mt-4 px-2">
                By clicking below, you acknowledge Solana Mobile's{' '}
                <a
                  href="https://solanamobile.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASSES}
                >
                  Privacy Policy
                </a>{' '}
                and agree to the{' '}
                <a
                  href="https://legal.solanamobile.com/en/skr-tos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASSES}
                >
                  Seeker Token Airdrop Terms
                </a>
                , including{' '}
                <a
                  href="https://legal.solanamobile.com/en/skr-tos#disputes"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASSES}
                >
                  BINDING ARBITRATION
                </a>{' '}
                on an individual, non-class basis.
              </p>
            )}
          </div>

          <div className="mt-6 rounded-lg bg-seeker-800 p-4">
            <Button
              variant="primary"
              onClick={onClaim}
              disabled={isDisabled}
              className="w-full"
            >
              {isClaiming ? (
                <>
                  <LoadingIcon label="Claiming" className="size-5" />
                  <span>Claiming…</span>
                </>
              ) : (
                'Claim SKR'
              )}
            </Button>

            {claimConfig.claimMessage && (
              <p className="text-seeker-400 text-center mt-4">
                {claimConfig.claimMessage}
              </p>
            )}
          </div>
        </PortalCard>
      </div>
    </div>
  );
};

const ClaimSuccessView = ({
  tokenAmount,
  walletAddress,
  txSignature,
}: {
  tokenAmount: number;
  walletAddress: string;
  txSignature: string;
}) => {
  const solscanBaseUrl = SOLANA_IS_MAINNET
    ? 'https://solscan.io'
    : 'https://solscan.io?cluster=devnet';

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4 py-8">
      <div className="flex flex-col items-center max-w-lg w-full">
        <div className="size-20 bg-emerald-400 rounded-[20px] flex items-center justify-center mb-8">
          <Check className="size-12 text-emerald-900" />
        </div>

        <p className="text-4xl font-normal text-white text-center leading-none tracking-tight m-0">
          You have claimed
        </p>
        <p className="text-4xl font-normal text-white mb-6 text-center leading-none tracking-tight m-0">
          {formatTokenAmount(tokenAmount)} SKR!
        </p>

        <p className="text-seeker-400 text-center text-sm leading-relaxed">
          Your SKR tokens can be found in the following wallet:
        </p>
        <p className="text-seeker-400 text-center text-sm mb-6 font-mono">
          {walletAddress.slice(0, 8)}...{walletAddress.slice(-8)}
        </p>

        <a
          href={`${solscanBaseUrl}/tx/${txSignature}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-10 flex items-center gap-1.5 rounded text-sm text-seeker-400 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        >
          View Transaction on Solscan
          <ExternalLink className="size-4" />
        </a>

        <div
          className="w-full rounded-2xl p-10 overflow-hidden relative"
          style={{
            backgroundColor: '#1D2323',
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse 120% 40% at 70% 100%, rgba(7, 112, 159, 0.6), transparent 60%),
                radial-gradient(ellipse 80% 25% at 70% 105%, rgba(18, 154, 139, 0.7), transparent 50%),
                radial-gradient(ellipse 60% 15% at 70% 110%, rgba(98, 243, 198, 0.5), transparent 40%),
                radial-gradient(ellipse 50% 10% at 70% 115%, rgba(246, 246, 245, 0.3), transparent 30%)
              `,
            }}
          />

          <div className="relative z-10">
            <h3 className="text-4xl font-normal text-[#95d2e6] leading-none tracking-tight">
              Stake SKR &amp;
            </h3>
            <h3 className="text-4xl font-normal leading-none tracking-tight mb-6">
              <span className="text-[#95d2e6]">Earn up to </span>
              <span className="text-[#faf9f4] [text-shadow:0_0_40px_rgba(250,249,244,0.5)]">
                25% APY
              </span>
            </h3>

            <p className="text-white text-lg leading-relaxed mb-10">
              Stake instantly, delegate to Guardians, support the Solana Mobile
              ecosystem, and earn rewards. Unstaking takes 48 hours.
            </p>

            <a
              href={STAKE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-white py-4 font-medium text-black transition-[background-color,transform] duration-150 ease-out hover:bg-seeker-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#1D2323]"
            >
              Stake Now
              <ExternalLink className="size-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ClaimSkrPage = () => {
  usePageTitle('Claim SKR | Publishing Portal');

  const trpc = useTRPC();
  const { connected, publicKey, signTransaction } = useWallet();
  const [claimState, setClaimState] = useState<ClaimState>('not_connected');
  const [claimData, setClaimData] = useState<ClaimData | null>(null);
  const [claimConfig, setClaimConfig] = useState<ClaimConfig>({
    claimEnabled: false,
    claimMessage: null,
  });
  const [error, setError] = useState<string | null>(null);
  const [txSignature, setTxSignature] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const walletAddress = publicKey?.toBase58() ?? null;

  const handleRetry = () => {
    setRetryKey((k) => k + 1);
  };

  useEffect(() => {
    if (!connected || !walletAddress) {
      setClaimState('not_connected');
      setClaimData(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const checkEligibility = async () => {
      setClaimState('loading');
      setError(null);

      try {
        const [configResult, eligibilityResult, claimStatusResult] =
          await Promise.all([
            trpc.skrAirdrop.getClaimConfig.query(undefined),
            trpc.skrAirdrop.checkWalletEligibility.query({
              walletAddress,
            }),
            trpc.skrAirdrop.getSkrClaimStatus.query({
              walletAddress,
              cluster: SOLANA_CLUSTER,
            }),
          ]);

        if (cancelled) return;

        setClaimConfig(configResult);

        if (claimStatusResult.claimed) {
          setClaimData({ tokenAmount: eligibilityResult.tokenAmount });
          setClaimState('already_claimed');
        } else if (eligibilityResult.eligible) {
          setClaimData({ tokenAmount: eligibilityResult.tokenAmount });
          setClaimState('eligible');
        } else {
          setClaimState('not_eligible');
        }
      } catch (err) {
        if (cancelled) return;
        const message = getErrorMessage(err, 'Failed to check eligibility');
        setError(message);
        setClaimState('error');
      }
    };

    checkEligibility();

    return () => {
      cancelled = true;
    };
  }, [connected, walletAddress, trpc, retryKey]);

  const handleClaim = async () => {
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    setClaimState('claiming');
    setError(null);

    try {
      toast.info('Fetching claim transaction...');

      const claimTxResult = await trpc.skrAirdrop.getClaimTx.query({
        claimant: publicKey.toBase58(),
        stake: 0,
      });

      if (!claimTxResult.tx || claimTxResult.tx.length === 0) {
        throw new Error('No transaction returned from claim API');
      }

      toast.info('Please sign the transaction in your wallet...');

      const txBuffer = Buffer.from(claimTxResult.tx[0], 'base64');
      const transaction = VersionedTransaction.deserialize(txBuffer);

      const signedTx = await signTransaction(transaction);

      toast.info('Submitting transaction...');

      const submitResult = await trpc.submitNftTransaction.mutate({
        signedTransaction: Buffer.from(signedTx.serialize()).toString('base64'),
      });

      if (submitResult._tag === 'Left') {
        throw new Error(submitResult.left.message);
      }

      const txSignatureResult = submitResult.right.transactionSignature;
      setTxSignature(txSignatureResult);

      await trpc.skrAirdrop.recordSkrClaim.mutate({
        walletAddress: publicKey.toBase58(),
        cluster: SOLANA_CLUSTER,
        transactionHash: txSignatureResult,
        tokenAmount: claimData?.tokenAmount ?? 0,
      });

      setClaimState('success');
      toast.success('SKR claimed successfully!');
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to claim SKR');

      const isUserRejection =
        message.toLowerCase().includes('user rejected') ||
        message.toLowerCase().includes('user denied') ||
        message.toLowerCase().includes('user cancelled');

      if (isUserRejection) {
        setClaimState('eligible');
        toast.info('Transaction cancelled');
      } else {
        setError(message);
        setClaimState('error');
        toast.error(message);
      }
    }
  };

  const renderContent = () => {
    switch (claimState) {
      case 'not_connected':
        return <NotConnectedView />;
      case 'loading':
        return <LoadingView />;
      case 'not_eligible':
        return <NotEligibleView />;
      case 'already_claimed':
        return <AlreadyClaimedView tokenAmount={claimData?.tokenAmount ?? 0} />;
      case 'error':
        return (
          <ErrorView error={error || 'Unknown error'} onRetry={handleRetry} />
        );
      case 'eligible':
      case 'claiming':
        return claimData ? (
          <ClaimView
            claimData={claimData}
            claimConfig={claimConfig}
            onClaim={handleClaim}
            isClaiming={claimState === 'claiming'}
          />
        ) : null;
      case 'success':
        return claimData && publicKey && txSignature ? (
          <ClaimSuccessView
            tokenAmount={claimData.tokenAmount}
            walletAddress={publicKey.toBase58()}
            txSignature={txSignature}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <PortalPage>
      <PortalPageHeader
        icon={<img src={tokenSkrIcon} alt="" className="size-8 rounded-full" />}
        title="Claim Your SKR"
      />

      {/* Re-keyed per view so each one fades/rises in instead of snapping
          (eligible ↔ claiming share ClaimView, so they don't replay). */}
      <div
        key={claimState === 'claiming' ? 'eligible' : claimState}
        className="motion-enter flex flex-1 flex-col"
      >
        {renderContent()}
      </div>

      <NetworkChip />
    </PortalPage>
  );
};
